import * as React from "react";
import ButtonBlack from "@shared/uiNew/Button/button_black";
import ButtonOrange from "@shared/uiNew/Button/button_orange";
import * as styles from "./UseCase.module.scss";

export type ExtendedSection = {
  heading?: string;
  paragraphs: string[];
  image?: string;
  imageAlt?: string;
  video?: string;
  imagePosition?: "left" | "right";
};

export type RelatedLink = {
  title: string;
  href: string;
};

type Props = {
  pill?: string;
  title: string;
  subtitle: string;
  intro?: string;

  feature: {
    subhead: string;
    image: string;
    imageAlt: string;
    bulletsTitle: string;
    bullets: string[];
    viewTemplateUrl?: string;
    panelUrl?: string;
  };

  sections?: ExtendedSection[];

  relatedTitle?: string;
  related?: RelatedLink[];

  cta?: {
    title: string;
    subtitle: string;
    primaryText: string;
    primaryHref: string;
    secondaryText?: string;
    secondaryHref?: string;
  };
};

export const UseCase = ({
  pill = "Ready-made chatbot flows",
  title,
  subtitle,
  intro,
  feature,
  sections = [],
  relatedTitle = "Other ready-made chatbot flows",
  related = [],
  cta = {
    title: "Launch this chatbot in your Dashly account",
    subtitle: "Start a 7-day free trial. No credit card required.",
    primaryText: "Start for free",
    primaryHref: "/panel/unauthorized/register",
    secondaryText: "Book a demo",
    secondaryHref: "/demo/",
  },
}: Props) => {
  return (
    <>
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          {pill && <span className={styles.pill}>{pill}</span>}
          <h1 className={styles.heroTitle}>{title}</h1>
          <p className={styles.heroSub}>{subtitle}</p>
          {intro && <p className={styles.heroIntro}>{intro}</p>}
        </div>
      </section>

      <section className={styles.feature}>
        <div className={styles.featureInner}>
          <div className={styles.featureCard}>
            <div>
              <h2 className={styles.featureSubhead}>{feature.subhead}</h2>
              <p className={styles.featureBulletsTitle}>{feature.bulletsTitle}</p>
              <ul className={styles.featureBullets}>
                {feature.bullets.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
              <div className={styles.featureButtons}>
                {feature.viewTemplateUrl && (
                  <ButtonBlack
                    icon="arrow"
                    text="View template"
                    href={feature.viewTemplateUrl}
                    target="_blank"
                  />
                )}
                {feature.panelUrl && (
                  <ButtonBlack
                    variant="outline"
                    text="Go to setup"
                    href={feature.panelUrl}
                    target="_blank"
                  />
                )}
              </div>
            </div>
            <img
              src={feature.image}
              alt={feature.imageAlt}
              className={styles.featureImage}
              loading="eager"
            />
          </div>
        </div>
      </section>

      {sections.map((sec, i) => {
        const reverse = sec.imagePosition === "left";
        const hasMedia = sec.image || sec.video;
        return (
          <section
            key={i}
            className={`${styles.section} ${i % 2 === 0 ? styles.alt : ""}`}
          >
            <div className={styles.sectionInner}>
              {hasMedia ? (
                <div className={`${styles.sectionRow} ${reverse ? styles.reverse : ""}`}>
                  <div>
                    {sec.heading && <h2 className={styles.sectionHeading}>{sec.heading}</h2>}
                    {sec.paragraphs.map((p, j) => (
                      <p key={j} className={styles.sectionText}>{p}</p>
                    ))}
                  </div>
                  {sec.video ? (
                    <video
                      className={styles.sectionVideo}
                      src={sec.video}
                      autoPlay
                      muted
                      loop
                      playsInline
                      controls={false}
                    />
                  ) : (
                    <img
                      src={sec.image}
                      alt={sec.imageAlt || ""}
                      className={styles.sectionImage}
                      loading="lazy"
                    />
                  )}
                </div>
              ) : (
                <>
                  {sec.heading && <h2 className={styles.sectionHeading}>{sec.heading}</h2>}
                  {sec.paragraphs.map((p, j) => (
                    <p key={j} className={styles.sectionText}>{p}</p>
                  ))}
                </>
              )}
            </div>
          </section>
        );
      })}

      {related.length > 0 && (
        <section className={styles.related}>
          <div className={styles.relatedInner}>
            <h2 className={styles.relatedHeading}>{relatedTitle}</h2>
            <div className={styles.relatedGrid}>
              {related.map((r) => (
                <a key={r.href} href={r.href} className={styles.relatedCard}>
                  <p className={styles.relatedTitle}>{r.title}</p>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className={styles.cta}>
        <div className={styles.ctaInner}>
          <h2 className={styles.ctaTitle}>{cta.title}</h2>
          <p className={styles.ctaSub}>{cta.subtitle}</p>
          <div className={styles.ctaButtons}>
            <ButtonOrange icon="arrow" text={cta.primaryText} href={cta.primaryHref} />
            {cta.secondaryText && cta.secondaryHref && (
              <ButtonOrange variant="outline" text={cta.secondaryText} href={cta.secondaryHref} />
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default UseCase;
